import { create } from "zustand";
import { API } from "@/lib/apiConfig";

const API_BASE = API.satelliteOverlay;

/**
 * Satellite Overlay Risk state interface.
 * Manages anti-corruption satellite overlay risk calculations.
 */
export interface SatelliteOverlayState {
  /** Project site identifier */
  projectSiteID: string;
  /** Construction progress percentage (0-100) */
  constructionProgressPercent: number;
  /** Budget disbursement percentage (0-100) */
  budgetDisbursementPercent: number;
  /** Satellite drift score (0-100) */
  satelliteDriftScore: number;
  /** Ghost site flag (true if suspicious pattern detected) */
  ghostSiteFlag: boolean;
  /** Land use mismatch percentage (0-100) */
  landUseMismatch: number;
  /** Procurement anomaly percentage (0-100) */
  procurementAnomaly: number;
  /** Cross-border suspicion level (0-5) */
  crossBorderSuspicion: number;
  /** Derived: Overlay risk index (0-100) */
  overlayRiskIndex: number;
  /** Derived: Progress mismatch (absolute difference) */
  progressMismatch: number;
  /** Set project site ID */
  setProjectSiteID: (id: string) => void;
  /** Set construction progress percent */
  setConstructionProgressPercent: (percent: number) => void;
  /** Set budget disbursement percent */
  setBudgetDisbursementPercent: (percent: number) => void;
  /** Set satellite drift score */
  setSatelliteDriftScore: (score: number) => void;
  /** Set land use mismatch */
  setLandUseMismatch: (mismatch: number) => void;
  /** Set procurement anomaly */
  setProcurementAnomaly: (anomaly: number) => void;
  /** Set cross-border suspicion */
  setCrossBorderSuspicion: (suspicion: number) => void;
  /** Compute overlay risk and update derived values */
  computeOverlayRisk: () => Promise<void>;
  /** Export satellite overlay data to JSON */
  exportSatelliteOverlay: () => void;
  /** Import satellite overlay data from JSON */
  importSatelliteOverlay: (data: {
    projectSiteID?: string;
    constructionProgressPercent?: number;
    budgetDisbursementPercent?: number;
    satelliteDriftScore?: number;
    landUseMismatch?: number;
    procurementAnomaly?: number;
    crossBorderSuspicion?: number;
  }) => void;
}

/**
 * Calculate progress mismatch between construction and budget.
 */
function calculateProgressMismatch(
  constructionProgress: number,
  budgetDisbursement: number
): number {
  return Math.abs(constructionProgress - budgetDisbursement);
}

/**
 * Determine if site is a ghost site.
 */
function calculateGhostSiteFlag(
  constructionProgress: number,
  budgetDisbursement: number
): boolean {
  return constructionProgress < 10 && budgetDisbursement > 30;
}

/**
 * Calculate overlay risk index.
 */
function calculateOverlayRiskIndex(
  progressMismatch: number,
  landUseMismatch: number,
  satelliteDriftScore: number,
  procurementAnomaly: number,
  crossBorderSuspicion: number
): number {
  const landUsePenalty = landUseMismatch * 0.6;
  const driftPenalty = satelliteDriftScore * 0.4;
  const procurementPenalty = procurementAnomaly * 0.5;

  const riskIndex =
    progressMismatch +
    landUsePenalty +
    driftPenalty +
    procurementPenalty +
    crossBorderSuspicion * 3;

  return Math.max(0, Math.min(100, riskIndex));
}

/**
 * Zustand store for Satellite Overlay Risk.
 */
export const useSatelliteOverlayStore = create<SatelliteOverlayState>((set, get) => ({
  projectSiteID: "",
  constructionProgressPercent: 0,
  budgetDisbursementPercent: 0,
  satelliteDriftScore: 0,
  ghostSiteFlag: false,
  landUseMismatch: 0,
  procurementAnomaly: 0,
  crossBorderSuspicion: 0,
  overlayRiskIndex: 0,
  progressMismatch: 0,

  setProjectSiteID: (id) => set({ projectSiteID: id }),

  setConstructionProgressPercent: (percent) =>
    set({ constructionProgressPercent: Math.max(0, Math.min(100, percent)) }),

  setBudgetDisbursementPercent: (percent) =>
    set({ budgetDisbursementPercent: Math.max(0, Math.min(100, percent)) }),

  setSatelliteDriftScore: (score) =>
    set({ satelliteDriftScore: Math.max(0, Math.min(100, score)) }),

  setLandUseMismatch: (mismatch) =>
    set({ landUseMismatch: Math.max(0, Math.min(100, mismatch)) }),

  setProcurementAnomaly: (anomaly) =>
    set({ procurementAnomaly: Math.max(0, Math.min(100, anomaly)) }),

  setCrossBorderSuspicion: (suspicion) =>
    set({ crossBorderSuspicion: Math.max(0, Math.min(5, suspicion)) }),

  computeOverlayRisk: async () => {
    const state = get();
    
    // Try to sync with backend if available
    try {
      const response = await fetch(`${API_BASE}/compute`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          projectSiteID: state.projectSiteID,
          constructionProgressPercent: state.constructionProgressPercent,
          budgetDisbursementPercent: state.budgetDisbursementPercent,
          satelliteDriftScore: state.satelliteDriftScore,
          landUseMismatch: state.landUseMismatch,
          procurementAnomaly: state.procurementAnomaly,
          crossBorderSuspicion: state.crossBorderSuspicion,
        }),
      });
      
      if (response.ok) {
        const data = await response.json();
        // Update state from backend response
        set({
          progressMismatch: Math.abs(data.state.constructionProgressPercent - data.state.budgetDisbursementPercent),
          ghostSiteFlag: data.state.ghostSiteFlag,
          overlayRiskIndex: data.state.overlayRiskIndex,
        });
        return;
      }
    } catch (error) {
      console.log("Backend not available, using local computation only");
    }
    
    // Fallback to local computation
    const progressMismatch = calculateProgressMismatch(
      state.constructionProgressPercent,
      state.budgetDisbursementPercent
    );
    const ghostSiteFlag = calculateGhostSiteFlag(
      state.constructionProgressPercent,
      state.budgetDisbursementPercent
    );
    const overlayRiskIndex = calculateOverlayRiskIndex(
      progressMismatch,
      state.landUseMismatch,
      state.satelliteDriftScore,
      state.procurementAnomaly,
      state.crossBorderSuspicion
    );

    set({
      progressMismatch,
      ghostSiteFlag,
      overlayRiskIndex,
    });
  },

  exportSatelliteOverlay: () => {
    const state = get();
    const data = {
      projectSiteID: state.projectSiteID,
      constructionProgressPercent: state.constructionProgressPercent,
      budgetDisbursementPercent: state.budgetDisbursementPercent,
      satelliteDriftScore: state.satelliteDriftScore,
      ghostSiteFlag: state.ghostSiteFlag,
      landUseMismatch: state.landUseMismatch,
      procurementAnomaly: state.procurementAnomaly,
      crossBorderSuspicion: state.crossBorderSuspicion,
      overlayRiskIndex: state.overlayRiskIndex,
    };

    const dateStr = new Date().toISOString().slice(0, 10).replace(/-/g, "");
    const filename = `satelliteOverlay_${dateStr}.json`;
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  },

  importSatelliteOverlay: (data) => {
    set((state) => ({
      ...state,
      projectSiteID: data.projectSiteID ?? state.projectSiteID,
      constructionProgressPercent: data.constructionProgressPercent ?? state.constructionProgressPercent,
      budgetDisbursementPercent: data.budgetDisbursementPercent ?? state.budgetDisbursementPercent,
      satelliteDriftScore: data.satelliteDriftScore ?? state.satelliteDriftScore,
      landUseMismatch: data.landUseMismatch ?? state.landUseMismatch,
      procurementAnomaly: data.procurementAnomaly ?? state.procurementAnomaly,
      crossBorderSuspicion: data.crossBorderSuspicion ?? state.crossBorderSuspicion,
    }));
    // Recompute after import
    get().computeOverlayRisk();
  },
}));



